"use client";

import { useEffect } from "react";

import { safeSessionSet } from "@/lib/browser-storage";

type BridgePayload = {
  formId: string;
  sourcePath: string;
  intent: string;
  pageCategory: string;
  offerType: string;
  planId: string;
  target: string;
  defaultPrompt: string;
  purchaseIntentScore: number;
  purchaseIntentBand: string;
  purchaseIntentReason: string;
};

const PLACEMENT = "free_chat_server_hero";
const VIEW_TRACK_DELAY_MS = 9000;

function currentPath(fallback: string) {
  return window.location.pathname || fallback;
}

function sessionSet(key: string, value: string | number | undefined) {
  if (value === undefined) return;
  safeSessionSet(key, String(value));
}

function promptFrom(form: HTMLFormElement | null, fallback: string) {
  const field = form?.elements.namedItem("question");
  if (field instanceof HTMLTextAreaElement) {
    return field.value.trim() || fallback;
  }
  return fallback;
}

export default function FreeChatHeroBridge({
  formId,
  sourcePath,
  intent,
  pageCategory,
  offerType,
  planId,
  target,
  defaultPrompt,
  purchaseIntentScore,
  purchaseIntentBand,
  purchaseIntentReason,
}: BridgePayload) {
  useEffect(() => {
    const form = document.getElementById(formId);
    if (!(form instanceof HTMLFormElement)) return undefined;

    const path = currentPath(sourcePath);
    const trackViewTimer = window.setTimeout(() => {
      void import("@/lib/analytics-seo-bridge")
        .then(({ trackSEOAgentBridgeViewed }) =>
          trackSEOAgentBridgeViewed({
            source_path: path,
            intent,
            page_category: pageCategory,
            offer_type: offerType,
            plan_id: planId,
            placement: PLACEMENT,
            purchase_intent_score: purchaseIntentScore,
            purchase_intent_band: purchaseIntentBand,
            purchase_intent_reason: purchaseIntentReason,
          })
        )
        .catch(() => {});
    }, VIEW_TRACK_DELAY_MS);

    function capture(prompt: string) {
      const finalPrompt = prompt.trim() || defaultPrompt;
      try {
        sessionSet("seo_entry_intent", intent);
        sessionSet("seo_entry_path", path);
        sessionSet("seo_entry_page_category", pageCategory);
        sessionSet("seo_entry_primary_offer", offerType);
        sessionSet("seo_entry_report_plan_id", planId);
        sessionSet("seo_entry_prompt", finalPrompt);
        sessionSet("seo_entry_auto_send", "1");
        sessionSet("seo_entry_purchase_intent_score", purchaseIntentScore);
        sessionSet("seo_entry_purchase_intent_band", purchaseIntentBand);
        sessionSet("seo_entry_purchase_intent_reason", purchaseIntentReason);
      } catch {
        // Storage unavailable: the anchor fallback still opens the birth-details form.
      }

      void import("@/lib/analytics-seo-bridge")
        .then(({ trackSEOAgentBridgeClicked, trackSEOIntentCaptured }) => {
          trackSEOAgentBridgeClicked({
            source_path: path,
            intent,
            page_category: pageCategory,
            offer_type: offerType,
            plan_id: planId,
            target,
            placement: PLACEMENT,
            purchase_intent_score: purchaseIntentScore,
            purchase_intent_band: purchaseIntentBand,
            purchase_intent_reason: purchaseIntentReason,
          });
          trackSEOIntentCaptured({
            source_path: path,
            intent,
            page_category: pageCategory,
            target,
            placement: PLACEMENT,
          });
        })
        .catch(() => {});

      window.dispatchEvent(new Event("seo-intent-refresh"));
      document
        .getElementById("start-reading")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    const onSubmit = (event: SubmitEvent) => {
      event.preventDefault();
      capture(promptFrom(form, defaultPrompt));
    };
    const promptButtons = Array.from(
      document.querySelectorAll<HTMLElement>(`[data-free-chat-prompt][data-form-id="${formId}"]`)
    );
    const onPromptClick = (event: Event) => {
      event.preventDefault();
      const button = event.currentTarget;
      if (!(button instanceof HTMLElement)) return;
      const prompt = button.dataset.freeChatPrompt || defaultPrompt;
      const field = form.elements.namedItem("question");
      if (field instanceof HTMLTextAreaElement) {
        field.value = prompt;
      }
      capture(prompt);
    };

    form.addEventListener("submit", onSubmit);
    for (const button of promptButtons) {
      button.addEventListener("click", onPromptClick);
    }

    return () => {
      window.clearTimeout(trackViewTimer);
      form.removeEventListener("submit", onSubmit);
      for (const button of promptButtons) {
        button.removeEventListener("click", onPromptClick);
      }
    };
  }, [
    defaultPrompt,
    formId,
    intent,
    offerType,
    pageCategory,
    planId,
    purchaseIntentBand,
    purchaseIntentReason,
    purchaseIntentScore,
    sourcePath,
    target,
  ]);

  return null;
}
